import { CookieManager, ServerOptions } from '@commercetools/frontend-sdk';
import { CookieValueTypes } from '@commercetools/frontend-sdk/lib/types/cookieHandling/CookieValueTypes';
import { TmpCookiesObj } from '@commercetools/frontend-sdk/lib/types/cookieHandling/TmpCookiesObj';
import { parse as parseCookie, serialize as serializeCookie } from 'cookie';

export default class OvveridedCookieManager implements CookieManager {
  static parseCookieString(cookieString: string): Record<string, string> {
    const cookiesObj: Record<string, string> = {};
    const cookiesArray: string[] = cookieString.split(';'); // Split the cookie string by ';' to get individual cookies

    cookiesArray.forEach((cookie) => {
      const [name, value] = cookie.trim().split('='); // Trim whitespace and split by '=' to separate names and values

      // Check if 'name' is not an attribute. This is a basic filter assuming that cookie names we're interested in won't match these attribute names.
      // This list can be extended based on requirements or knowledge about the cookie structure you're dealing with.
      const attributes = ['Path', 'Expires', 'Domain', 'Secure', 'HttpOnly', 'SameSite'];
      if (name && value && !attributes.includes(name)) {
        cookiesObj[name] = decodeURIComponent(value); // Decode the cookie value and add to the object
      }
    });

    return cookiesObj;
  }

  static stringify(value = '') {
    try {
      const result = JSON.stringify(value);
      return /^[{[]/.test(result) ? result : value;
    } catch (e) {
      return value;
    }
  }
  static isClientSide() {
    return typeof window !== 'undefined';
  }
  static processValue(value: string) {
    switch (value) {
      case 'true':
        return true;
      case 'false':
        return false;
      case 'undefined':
        return void 0;
      case 'null':
        return null;
      default:
        return value;
    }
  }
  static decode(str: string) {
    return str ? str.replace(/(%[0-9A-Z]{2})+/g, decodeURIComponent) : str;
  }

  private parseOptions(options?: ServerOptions) {
    return options ? { req: options.req, res: options.res, ...options } : { req: undefined, res: undefined };
  }

  private createCookieString(key: string, data: any, cookieOptions: any): string {
    return serializeCookie(key, OvveridedCookieManager.stringify(data), {
      path: '/',
      secure: true,
      sameSite: 'None',
      ...cookieOptions,
    });
  }

  private handleServerSideCookie({
    req,
    res,
    cookieStr,
    key,
    data,
  }: {
    req: any;
    res: any;
    cookieStr: string;
    key: string;
    data: any;
  }) {
    if (!res || !req) return;

    this.appendCookieToResponse(res, cookieStr);
    this.updateRequestCookies(req, key, data);
  }

  private appendCookieToResponse(res: any, cookieStr: string) {
    let currentCookies = res.getHeader('Set-Cookie') || [];
    if (typeof currentCookies === 'string') {
      currentCookies = [currentCookies];
    }
    res.setHeader('Set-Cookie', [...currentCookies, cookieStr]);
  }

  private updateRequestCookies(req: any, key: string, data: any) {
    if (req.cookies) {
      if (data === '') {
        delete req.cookies[key];
      } else {
        req.cookies[key] = OvveridedCookieManager.stringify(data);
      }
    }

    if (req.headers?.cookie) {
      const parsedCookies = parseCookie(req.headers.cookie);
      if (data === '') {
        delete parsedCookies[key];
      } else {
        parsedCookies[key] = OvveridedCookieManager.stringify(data);
      }
      req.headers.cookie = this.serializeCookies(parsedCookies);
    }
  }

  private serializeCookies(cookies: { [key: string]: string }): string {
    return Object.entries(cookies).reduce((accum, [key, value]) => `${accum}${key}=${value};`, '');
  }
  async getCookies(options?: ServerOptions): Promise<TmpCookiesObj> {
    let req;
    if (options) {
      req = options.req;
    }
    if (!OvveridedCookieManager.isClientSide()) {
      if (req?.cookies) {
        return req.cookies;
      }
      if (req?.headers?.cookie) {
        return parseCookie(req.headers.cookie);
      }
      return {};
    }
    const _cookies = {} as any;
    const documentCookies = document.cookie ? document.cookie.split('; ') : [];
    for (let i = 0, len = documentCookies.length; i < len; i++) {
      const cookieParts = documentCookies[i].split('=');
      const _cookie = cookieParts.slice(1).join('=');
      const name = cookieParts[0];
      _cookies[name] = _cookie;
    }
    if (OvveridedCookieManager.isClientSide()) {
      const cookieStrfromSession = sessionStorage.getItem('cookieStr');
      if (cookieStrfromSession) {
        return JSON.parse(cookieStrfromSession);
      }
    }
    return _cookies;
  }

  async getCookie(key: string, options?: ServerOptions): Promise<CookieValueTypes> {
    const _cookies = (await this.getCookies(options)) as any;
    const value = _cookies[key];
    if (value === void 0) {
      return void 0;
    }
    return OvveridedCookieManager.processValue(OvveridedCookieManager.decode(value));
  }
  async setCookie(key: string, data: any, options?: ServerOptions): Promise<void> {
    const { req, res, ...cookieOptions } = this.parseOptions(options);
    const cookieStr = this.createCookieString(key, data, cookieOptions);

    if (!OvveridedCookieManager.isClientSide()) {
      this.handleServerSideCookie({ req, res, cookieStr, key, data });
    } else {
      sessionStorage.setItem('cookieStr', JSON.stringify(OvveridedCookieManager.parseCookieString(cookieStr)));
      document.cookie = `${cookieStr}; Partitioned;`;
    }
  }

  async deleteCookie(key: string, options?: ServerOptions): Promise<void> {
    await this.setCookie(key, '', { ...options, maxAge: -1 } as any);
  }

  async hasCookie(key: string, options?: ServerOptions): Promise<boolean> {
    if (!key) {
      return false;
    }
    const cookie = await this.getCookies(options);
    return cookie.hasOwnProperty(key);
  }
}
